import React, { useCallback, useRef } from "react";
import type { FC } from "react";

import cn from "classnames";
import { createPortal } from "react-dom";

import {
    DEFAULT_ADJUSTMENT_LOCKED,
    DEFAULT_ALIGNMENT,
    DEFAULT_SIDE,
    DEFAULT_VIEW,
    DEFAULT_WIDTH,
    POPUP_ARIA_LIVE,
    POPUP_ROLE,
    POPUP_TEST_ID,
} from "./constants/values";
import { PopUpTail } from "../../helpers/side/PopUpTail/PopUpTail";
import usePopUpPosition from "../../hooks/usePopUpPosition";
import { extractProps } from "../../utils/react";

import type { PopUpProps } from "./types";

import classes from "./PopUp.styl";

const POPUP_DISPLAY_NAME = "PopUp";

/**
 * Поп-ап. Обертка с заложенными токенами цвета и отступами для всплывающих элементов интерфейса
 *
 * <a href="https://www.figma.com/design/vTg3KAvmAXA9LigeyiqCWL/%F0%9F%92%A0-Base-Components?node-id=2405-335934&t=eEgheFYxKBmtY1P8-0" target="_blank">Спека</a>
 * @constructor
 */
export const PopUp: FC<PopUpProps> = (
    props: PopUpProps,
): React.ReactElement | null => {
    const {
        view = DEFAULT_VIEW,
        width = DEFAULT_WIDTH,
        side = DEFAULT_SIDE,
        alignment = DEFAULT_ALIGNMENT,
        adjustmentLocked = DEFAULT_ADJUSTMENT_LOCKED,
        targetRef,
        open,
        children,
        id,
        onOpen,
        onClose,
        onBeforePositionRecalculation,
        customVarsStyles,
        delay,
        "data-tsid": tsid,
    } = props;
    const { customColorsStyles } = extractProps(props, ["customColorsStyles"]);
    const popUpRef = useRef<HTMLDivElement | null>(null);

    const enterAnimationCallback = useCallback(() => {
        if (!popUpRef.current) return undefined;

        popUpRef.current.classList.add(classes[`__animation-enter`]);
        popUpRef.current.classList.remove(classes[`__animation-exit`]);
    }, [popUpRef]);
    const exitAnimationCallback = useCallback(() => {
        if (!popUpRef.current) return undefined;

        popUpRef.current.classList.add(classes[`__animation-exit`]);
        popUpRef.current.classList.remove(classes[`__animation-enter`]);
    }, [popUpRef]);

    const { position, renderPopUp, wrapperStyles } = usePopUpPosition({
        side,
        alignment,
        adjustmentLocked,
        targetRef,
        onOpen,
        onClose,
        onBeforePositionRecalculation,
        open,
        popUpRef,
        enterAnimationCallback,
        exitAnimationCallback,
        delay,
    });

    const wrapperClasses = cn(
        classes.wrapper,
        classes[`__view-${view}`],
        classes[`__side-${position}`],
        classes[`__alignment-${alignment}`],
        classes[`__placement-${position}-${alignment}`],
        customColorsStyles,
        customVarsStyles,
    );

    if (!renderPopUp && !open) return null;

    return createPortal(
        <div
            id={id}
            className={wrapperClasses}
            style={wrapperStyles}
            ref={popUpRef}
            role={POPUP_ROLE}
            aria-live={POPUP_ARIA_LIVE}
            data-tsid={tsid || POPUP_TEST_ID}
            data-uikit={POPUP_DISPLAY_NAME}
        >
            <PopUpTail side={position} alignment={alignment} />
            <div className={classes.popup} style={{ width }}>
                {children}
            </div>
        </div>,
        document.body,
    );
};

PopUp.displayName = POPUP_DISPLAY_NAME;

export default PopUp;
